import { useInventoryItem } from '../../hooks/useInventory';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
  MetricCard,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Skeleton,
} from '@tonic/shared/components/ui';
import { StatusBadge } from '@/features/shared/components/data-display/StatusBadge';
import { PageTitle } from '@/components/ui/PageTitle';
import { ArrowLeft, Edit, History, Package, Tag, Truck } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { UpdateStatusDialog } from './UpdateStatusDialog';
import { EditItemDialog } from './EditItemDialog';
import { ItemHistory } from './ItemHistory';
import { ItemTransfers } from './ItemTransfers';
import { useState } from 'react';

/**
 * Component that displays detailed information about an inventory item
 * @returns {JSX.Element} The inventory detail component
 */
export function InventoryDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { data: item, isLoading } = useInventoryItem(id);
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  if (isLoading) {
    return <InventoryDetailSkeleton />;
  }

  if (!item) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <Package className="h-12 w-12 text-muted-foreground mb-4" />
        <h2 className="text-2xl font-bold mb-2">Item Not Found</h2>
        <p className="text-muted-foreground mb-4">
          The inventory item you're looking for doesn't exist.
        </p>
        <Button onClick={() => navigate('/inventory')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Inventory
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={item.name}
        description={`SKU: ${item.sku} • Category: ${item.category}`}
        breadcrumbs={
          <Button variant="ghost" size="sm" onClick={() => navigate('/inventory')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Inventory
          </Button>
        }
        actions={
          <>
            <Button variant="outline" onClick={() => setIsStatusDialogOpen(true)}>
              <Tag className="mr-2 h-4 w-4" />
              Update Status
            </Button>
            <Button onClick={() => setIsEditDialogOpen(true)}>
              <Edit className="mr-2 h-4 w-4" />
              Edit Item
            </Button>
          </>
        }
      />

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="Status"
          value={<StatusBadge status={item.status} className="text-lg" />}
        />
        <MetricCard title="Quantity" value={item.quantity} icon={Package} />
        <MetricCard title="Location" value={item.location.name} description={item.location.code} />
        <MetricCard
          title="Last Updated"
          value={new Date(item.updatedAt).toLocaleDateString()}
          description={new Date(item.updatedAt).toLocaleTimeString()}
        />
      </div>

      <Tabs defaultValue="details" className="space-y-4">
        <TabsList>
          <TabsTrigger value="details">
            <Package className="mr-2 h-4 w-4" />
            Details
          </TabsTrigger>
          <TabsTrigger value="history">
            <History className="mr-2 h-4 w-4" />
            History
          </TabsTrigger>
          <TabsTrigger value="transfers">
            <Truck className="mr-2 h-4 w-4" />
            Transfers
          </TabsTrigger>
        </TabsList>
        <TabsContent value="details" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Item Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium mb-1">Description</h4>
                  <p className="text-sm text-muted-foreground">
                    {item.description || 'No description provided'}
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-1">Specifications</h4>
                  <div className="text-sm text-muted-foreground">
                    {item.specifications ? (
                      <pre className="whitespace-pre-wrap">
                        {JSON.stringify(item.specifications, null, 2)}
                      </pre>
                    ) : (
                      'No specifications provided'
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="history">
          <ItemHistory itemId={id} />
        </TabsContent>
        <TabsContent value="transfers">
          <ItemTransfers itemId={id} />
        </TabsContent>
      </Tabs>

      <UpdateStatusDialog
        item={item}
        open={isStatusDialogOpen}
        onOpenChange={setIsStatusDialogOpen}
      />
      <EditItemDialog item={item} open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen} />
    </div>
  );
}

/**
 * Skeleton loading state for the inventory detail view
 * @returns {JSX.Element} The skeleton component
 */
function InventoryDetailSkeleton() {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Skeleton className="h-10 w-1/3" />
        <Skeleton className="h-4 w-1/4" />
      </div>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <Skeleton className="h-4 w-20" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-24" />
            </CardContent>
          </Card>
        ))}
      </div>
      <Skeleton className="h-[400px]" />
    </div>
  );
}
